import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Clapperboard, Play, Sparkles, Film, Star } from 'lucide-react';

const AIRA_LOGO = "/aira-echo-logo.png";

export default function FilmStudioSection() {
  const navigate = useNavigate();

  const openFilmStudio = () => {
    navigate('/film-studio');
  };

  // Features list
  const features = [
    { icon: Film, label: '10+ Genres' },
    { icon: Sparkles, label: 'AI Powered' },
    { icon: Star, label: '8K Output' },
  ];

  return (
    <section className="relative z-10 py-20 px-6 overflow-hidden">
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[400px] bg-gradient-to-r from-purple-600/10 via-pink-500/10 to-cyan-500/10 blur-[100px] rounded-full" />
      </div>

      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/20 to-pink-500/20 border border-purple-500/30 text-purple-400 text-sm font-medium mb-6">
            <Clapperboard className="w-4 h-4" />
            AI Film Studio
          </span>
        </motion.div>

        {/* Main Creative Box */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative"
        >
          {/* Animated Gradient Border */}
          <motion.div
            className="absolute -inset-1 rounded-3xl opacity-75 blur-lg"
            style={{
              background: 'linear-gradient(90deg, #9333EA, #EC4899, #06B6D4, #9333EA)',
              backgroundSize: '300% 100%',
            }}
            animate={{
              backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'],
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: 'linear',
            }}
          />
          
          {/* Inner Glow */}
          <motion.div
            className="absolute -inset-0.5 rounded-3xl opacity-40 blur-sm"
            style={{
              background: 'linear-gradient(135deg, #9333EA, #EC4899)',
            }}
            animate={{
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />

          {/* Main Content Box */}
          <div className="relative bg-gradient-to-br from-[#0a0015]/95 via-[#0f0720]/95 to-[#0a0015]/95 backdrop-blur-xl rounded-3xl border border-white/10 overflow-hidden">
            <div className="grid lg:grid-cols-2 gap-8 p-8 md:p-12">
              {/* Left Side - Content */}
              <div className="flex flex-col justify-center">
                {/* Logo & Title */}
                <div className="flex items-center gap-4 mb-6">
                  <motion.div
                    animate={{
                      filter: [
                        'drop-shadow(0 0 10px rgba(147, 51, 234, 0.4))',
                        'drop-shadow(0 0 20px rgba(236, 72, 153, 0.6))',
                        'drop-shadow(0 0 10px rgba(147, 51, 234, 0.4))',
                      ],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  >
                    <img 
                      src={AIRA_LOGO} 
                      alt="AIRA" 
                      className="w-16 h-16 object-contain"
                    />
                  </motion.div>
                  <div>
                    <p className="text-purple-400 text-sm font-medium tracking-wider uppercase">AIRA AI Studios</p>
                    <motion.h2 
                      className="font-['Playfair_Display'] text-3xl md:text-4xl font-bold text-white"
                      animate={{
                        textShadow: [
                          '0 0 10px rgba(147, 51, 234, 0.3)',
                          '0 0 25px rgba(236, 72, 153, 0.5)',
                          '0 0 10px rgba(147, 51, 234, 0.3)',
                        ],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut',
                      }}
                    >
                      Create Your Own Film
                    </motion.h2>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-400 text-lg mb-8 leading-relaxed">
                  Transform your imagination into cinema. AI-powered storytelling, 
                  character creation, and stunning visuals — all in one studio.
                </p>

                {/* Feature Tags */}
                <div className="flex flex-wrap gap-3 mb-8">
                  {features.map((feat, idx) => (
                    <motion.span
                      key={idx}
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                      className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-300 text-sm"
                    >
                      <feat.icon className="w-4 h-4 text-purple-400" />
                      {feat.label}
                    </motion.span>
                  ))}
                </div>

                {/* CTA Button */}
                <motion.button
                  onClick={openFilmStudio}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white font-semibold text-lg hover:shadow-[0_0_40px_rgba(147,51,234,0.4)] transition-all group w-fit"
                  data-testid="film-studio-trigger"
                >
                  <Play className="w-5 h-5 group-hover:scale-110 transition-transform" />
                  Enter Film Studio
                  <motion.span
                    animate={{ x: [0, 5, 0] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                    className="ml-1"
                  >
                    →
                  </motion.span>
                </motion.button>
              </div>

              {/* Right Side - Visual */}
              <div className="relative flex items-center justify-center">
                {/* Animated Film Reel / Clapperboard Visual */}
                <div className="relative">
                  {/* Glowing Orbs */}
                  <motion.div
                    className="absolute -top-10 -right-10 w-32 h-32 rounded-full bg-purple-500/20 blur-3xl"
                    animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
                    transition={{ duration: 3, repeat: Infinity }}
                  />
                  <motion.div
                    className="absolute -bottom-10 -left-10 w-32 h-32 rounded-full bg-pink-500/20 blur-3xl"
                    animate={{ scale: [1.2, 1, 1.2], opacity: [0.3, 0.5, 0.3] }}
                    transition={{ duration: 3, repeat: Infinity, delay: 1 }}
                  />

                  {/* Central Icon */}
                  <motion.div
                    initial={{ rotate: -10 }}
                    whileInView={{ rotate: 0 }}
                    viewport={{ once: true }}
                    className="relative z-10 w-48 h-48 rounded-3xl bg-gradient-to-br from-purple-600/30 to-pink-600/30 border border-white/20 flex items-center justify-center backdrop-blur-sm"
                  >
                    <Clapperboard className="w-24 h-24 text-white/80" />
                    
                    {/* Floating elements */}
                    <motion.div
                      className="absolute -top-4 -right-4 p-3 rounded-xl bg-gradient-to-r from-purple-500 to-pink-500"
                      animate={{ y: [0, -5, 0] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <Sparkles className="w-5 h-5 text-white" />
                    </motion.div>
                    <motion.div
                      className="absolute -bottom-4 -left-4 p-3 rounded-xl bg-gradient-to-r from-cyan-500 to-blue-500"
                      animate={{ y: [0, 5, 0] }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                    >
                      <Film className="w-5 h-5 text-white" />
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </div>

            {/* Decorative Corner Accents */}
            <div className="absolute top-4 left-4 w-8 h-8 border-l-2 border-t-2 border-purple-500/50 rounded-tl-xl" />
            <div className="absolute top-4 right-4 w-8 h-8 border-r-2 border-t-2 border-pink-500/50 rounded-tr-xl" />
            <div className="absolute bottom-4 left-4 w-8 h-8 border-l-2 border-b-2 border-pink-500/50 rounded-bl-xl" />
            <div className="absolute bottom-4 right-4 w-8 h-8 border-r-2 border-b-2 border-purple-500/50 rounded-br-xl" />
          </div>
        </motion.div>

        {/* Genre Preview Pills */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3 mt-8"
        >
          {['Romance', 'Action', 'Horror', 'Fantasy', 'Thriller', 'Sci-Fi', 'Anime', 'Drama'].map((genre, idx) => (
            <span
              key={genre}
              className="px-4 py-2 rounded-full bg-white/5 border border-white/10 text-gray-400 text-sm hover:border-purple-500/50 hover:text-purple-400 transition-colors cursor-pointer"
            >
              {genre}
            </span>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
